import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { MetricCategory } from '@chiroup/core/constants/metricCategories';

export type EventLogClinic = {
  clinicId: number;
  clinicName: string;
  status: string;
  plan: string;
};

export type EventLogEvent = {
  id: number;
  clinicId: number;
  userId: string | null;
  locationId: number | null;
  eventType: string;
  metadata: Record<string, unknown> | null;
  occurredAt: number;
  userName: string | null;
};

export type EventLogUser = {
  userId: string;
  userName: string;
  role: string;
  metrics: Record<string, number>;
  daily: number[];
  totalActivity: number;
};

export type EventLogLocation = {
  locationId: number;
  locationName: string;
};

export type Granularity = 'daily' | 'weekly' | 'monthly';

export type EventLogClinicDashboard = {
  clinic: {
    ID: number;
    name: string;
    status: string;
    plan: string;
    subscriptionStartDate?: string | null;
  } | null;
  metrics: Record<string, number>;
  previousMetrics: Record<string, number>;
  categoryTotals: Record<MetricCategory, number>;
  kpis: {
    totalEvents: number;
    activeUsers: number;
    timeInAppMinutes: number;
    daysActive: number;
    rangeDays: number;
  };
  previousKpis: {
    totalEvents: number;
    activeUsers: number;
    timeInAppMinutes: number;
    daysActive: number;
  };
  periodStart: string;
  periodEnd: string;
  previousPeriodStart: string;
  previousPeriodEnd: string;
  dates: string[];
  daily: { date: string; total: number }[];
  dailyByMetric: Record<string, number[]>;
  granularity: Granularity;
  users: EventLogUser[];
  usersDates: string[];
  locations: EventLogLocation[];
};

export type UserLevelActivity = {
  logins: {
    currTotal: number;
    prevTotal: number;
    daily: number[];
  };
  signatures: {
    currTotal: number;
    prevTotal: number;
    daily: number[];
  };
  dates: string[];
};

const eventLogService = () => {
  const listClinics = async (params: {
    search?: string;
    plan?: string;
    status?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ data: EventLogClinic[]; skip?: number; count: number }> => {
    return ChiroUpAPI.get('api', '/event-logs/clinics', { queryParams: params });
  };

  const listEvents = async (
    clinicId: number,
    params: {
      skip?: number;
      limit?: number;
      eventType?: string;
      userId?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<{ data: EventLogEvent[]; skip?: number; count: number }> => {
    return ChiroUpAPI.get('api', `/event-logs/clinics/${clinicId}/events`, {
      queryParams: params,
    });
  };

  const getDashboard = async (
    clinicId: number,
    params: {
      startDate?: string;
      endDate?: string;
      userId?: string;
      locationId?: string;
    }
  ): Promise<EventLogClinicDashboard> => {
    return ChiroUpAPI.get('api', `/event-logs/clinics/${clinicId}/dashboard`, {
      queryParams: params,
    });
  };

  const getUserLevelActivity = async (params: {
    userId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<UserLevelActivity> => {
    return ChiroUpAPI.get('api', '/event-logs/users/activity', {
      queryParams: params,
    });
  };

  return {
    listClinics,
    listEvents,
    getDashboard,
    getUserLevelActivity,
  };
};

export default eventLogService();
